import React, { useEffect, useState } from "react";
import { BsArrowLeft, BsCheck2Circle } from "react-icons/bs";
import { useUser } from "../../context/user-context";
import { toast } from "react-hot-toast";
import useAuthAxios from "../../utils/baseAxios";
import { API } from "../../constants/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { errorMessage } from "../../utils/errorMessage";
import ClipLoader from "react-spinners/ClipLoader";
import OtpPin from "../../components/OtpPin";
import {
  MAX_E164_DIGITS,
  parseToE164Phone,
  sanitizePhoneInput,
} from "../../utils/phoneE164";

const EditProfile = ({ setOpenEditProfile, setOpenAddPhone }: any) => {
  const { userDetails } = useUser();
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [otpScreen, setOtpScreen] = useState<boolean>(false);
  const axiosInstance = useAuthAxios();
  const [otp, setOtp] = useState<string>("");
  const [isAvailable, setIsAvailable] = useState(null);

  const [usernameAdded, setUsernameAdded] = useState<boolean>(false);
  const [deexTag, setDeexTag] = useState<string>("");
  const [fullName, setFullName] = useState<string>("");

  const queryClient = useQueryClient();
  const [minutes, setMinutes] = useState<number>(0);
  const [seconds, setSeconds] = useState<number>(59);
  const [sendOtp, setSendOtp] = useState<boolean>(false);
  const firstInitial = userDetails?.user?.profile?.firstName || "";
  const secondInitial = userDetails?.user?.profile?.lastName || "";
  const username = userDetails?.user?.profile?.username;
  useEffect(() => {
    setFullName(`${firstInitial} ${secondInitial}`.trim());
  }, [firstInitial, secondInitial]);
  useEffect(() => {
    if (username) {
      setUsernameAdded(true);
    }
  }, [username]);
  const updateProfile = async (data: any) => {
    const response = await axiosInstance.patch(API.profile, data);
    return response.data;
  };
  const completeUpdateProfile = useMutation({
    mutationFn: updateProfile,
    onSuccess: (r) => {
      toast.success(r.message);
      queryClient.invalidateQueries({
        queryKey: ["userDetails"],
      });
      setTimeout(() => {
        setOpenEditProfile(false);
        setOtpScreen(false);
      }, 2000);
    },
    onError: (e) => {
      // console.log(e);
      const err = e as any;
      toast.error(errorMessage(err?.message || err?.data?.message));
    },
  });
  const sendProfileOTP = async ({ purpose }: any) => {
    const response = await axiosInstance.post(API.requestOTP, {
      purpose,
    });
    return response.data;
  };
  const completeSendProfileOtp = useMutation({
    mutationFn: sendProfileOTP,
    onSuccess: (r) => {
      toast.success(r.message);

      setOtpScreen(true);
    },
    onError: (e) => {
      // console.log(e);
      const err = e as any;
      toast.error(errorMessage(err?.message || err?.data?.message));
    },
  });

  useEffect(() => {
    const interval = setInterval(() => {
      if (seconds === 0) {
        if (minutes === 0) {
          clearInterval(interval);
        } else {
          setSeconds(59);
          setMinutes(minutes - 1);
        }
      } else {
        setSeconds(seconds - 1);
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [seconds, minutes]);
  useEffect(() => {
    if (seconds > 0 || minutes > 0) {
      setSendOtp(true);
    } else {
      setSendOtp(false);
    }
  }, [seconds, minutes]);
  const resendOtp = () => {
    axiosInstance
      .post(API.requestOTP, {
        purpose: "edit-profile",
      })
      .then((r: any) => {})
      .catch((e: any) => {
        console.log(e.response.data.message);
      });

    setMinutes(0);
    setSeconds(59);
  };

  const getUsernameAvail = async () => {
    const response = await axiosInstance.get(API.checkUsername(deexTag));

    setIsAvailable(response.data.data.isAvailable);
    return response.data;
  };
  const { error: error3, refetch } = useQuery({
    queryKey: ["check-username"],
    queryFn: getUsernameAvail,
    enabled: deexTag.length > 3, // Prevents the query from running if cardId is not set
    retry: 1,
  });
  useEffect(() => {
    // Call refetch whenever deexTag changes and its length is greater than 3
    if (deexTag.length > 3) {
      refetch();
    } else {
      setIsAvailable(null);
    }
  }, [deexTag, refetch]);
  useEffect(() => {
    if (error3) {
      const newError = error3 as any;
      toast.error(errorMessage(newError?.message || newError?.data?.message));
    }
  }, [error3]);

  const validateTagLength = (deexTag: string) => {
    if (deexTag.length < 3) {
      toast.error("Deextag length must be greater than 3");

      return false;
    }
    return true;
  };
  const sendTag = async (data: any) => {
    const response = await axiosInstance.patch(API.addUsername, data);
    return response.data;
  };
  const completeSendTag = useMutation({
    mutationFn: sendTag,
    onSuccess: (r) => {
      toast.success(r.message);
      queryClient.invalidateQueries({ queryKey: ["userDetails"] });
    },
    onError: (e) => {
      // console.log(e);
      const err = e as any;
      toast.error(errorMessage(err?.message || err?.data?.message));
    },
  });

  const handleAddUsername = () => {
    if (!validateTagLength(deexTag)) {
      return;
    }
    if (isAvailable) {
      const data = {
        username: deexTag,
      };
      completeSendTag.mutate(data);
    }
  };

  const kycData = userDetails?.user?.kyc?.data;
  const kycStatus = userDetails?.user?.kyc?.status.toLowerCase();
  const isNameLocked =
    kycData && Object.keys(kycData).length > 0 && kycStatus === "verified";

  return (
    <div className="fixed inset-0  flex font-manrope justify-start items-start pt-16 overflow-auto pb-16 bg-[#E7EBEE] dark:bg-primary_dark dark:bg-opacity-95  backdrop-blur-sm">
      <div
        className={` w-11/12 mds:w-8/12 md:7/12 border dark:border-[#303030] bg-white  rounded-xl mx-auto p-6 dark:bg-[#192025]   lgss:w-2/5 xxl:w-1/3 `}
      >
        <div className="w-full flex justify-start gap-6 items-center">
          <button
            onClick={() => {
              if (otpScreen) {
                setOtpScreen(false);
                setOtp("");
              } else {
                setOpenEditProfile(false);
                setOtpScreen(false);
              }
            }}
          >
            <BsArrowLeft className="text-black dark:text-white text-[20px]" />
          </button>
          <h4 className="text-gray-900 dark:text-gray-100 font-sora font-semibold text-[20px]">
            My Profile
          </h4>
        </div>
        {otpScreen ? (
          <div className="mt-10 w-full">
            <h4 className="font-manrope w-10/12 mb-6 dark:text-gray-200 text-gray-800 mx-auto text-center font-medium text-[14px] ">
              Enter the 6-digit verification code sent to your email to verify
              this action.
              <br />
              Not receiving OTP? Chack spam folder.
            </h4>
            <OtpPin otp={otp} setOtp={setOtp} input={6} />
            <div className="flex justify-center mt-4 items-center">
              {seconds > 0 || minutes > 0 ? (
                <p className="text-paragraph dark:text-gray-200  font-bold text-[12px]">
                  Time Remaining:{" "}
                  <span className="text-secondary_button">
                    {minutes < 10 ? `0${minutes}` : minutes}:{" "}
                    {seconds < 10 ? `0${seconds}` : seconds}
                  </span>
                </p>
              ) : (
                <p className="font-bold  text-paragraph dark:text-gray-200 text-[12px]">
                  Didn't recieve code?
                </p>
              )}
              {!sendOtp && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    resendOtp();
                  }}
                  className="text-secondary_button text-[12px] flex justify-center items-center ml-2 font-bold"
                >
                  Resend OTP
                </button>
              )}
            </div>
            <button
              onClick={() => {
                const trimmed = fullName.trim();
                const nameParts = trimmed.split(/\s+/);
                const firstName = nameParts[0] || "";
                const lastName = nameParts.slice(1).join(" ") || "";

                const payload: any = { otp };
                if (!isNameLocked && trimmed.length > 0) {
                  payload.firstName = firstName;
                  payload.lastName = lastName;
                }
                if (phoneNumber.trim()) {
                  const e164 = parseToE164Phone(phoneNumber);
                  if (e164) {
                    payload.phoneNumber = e164;
                  }
                }

                completeUpdateProfile.mutate(payload);
              }}
              disabled={otp.length !== 6 || completeUpdateProfile.isPending}
              className={
                otp.length !== 6
                  ? "w-full bg-gray-400 rounded-xl flex justify-center items-center text-gray-100 text-[15px] font-medium mt-24   h-[48px]"
                  : "w-full bg-secondary_button rounded-xl flex justify-center items-center text-white text-[15px] font-medium mt-24 h-[48px]"
              }
            >
              {completeUpdateProfile.isPending ? (
                <ClipLoader color="#ffffff" size={20} />
              ) : (
                "Update profile"
              )}
            </button>
          </div>
        ) : (
          <div className="mt-8 w-full">
            <div className="w-full flex flex-col items-center justify-center">
              <div className="bg-white dark:bg-[#1F2C36] rounded-full p-2 flex justify-center items-center">
                <div className="w-[84px] h-[84px] rounded-full bg-[#D4EBFD] flex justify-center items-center font-sora font-semibold text-secondary_button text-[32px]">
                  {firstInitial.charAt(0)}
                  {secondInitial.charAt(0)}
                </div>
              </div>
            </div>
            <div className="mt-10">
              <h4 className="text-gray-800 dark:text-gray-200 text-[15px] font-medium">
                Full Name
              </h4>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={isNameLocked}
                placeholder="Enter your full name"
                className={
                  isNameLocked
                    ? "h-[50px] dark:bg-[#1F2B36] dark:text-gray-200 flex justify-start items-center bg-gray-50 w-full mt-2 px-[16px] font-manrope border rounded-xl border-gray-200 dark:border-gray-600 outline-none text-gray-700 transition duration-200 cursor-not-allowed"
                    : "h-[50px] dark:bg-[#1F2B36] dark:text-gray-200 bg-gray-50 w-full mt-2 px-[16px] font-manrope border rounded-xl border-gray-200 dark:border-gray-600 outline-none text-gray-700 transition duration-200"
                }
              />
            </div>
            <div className="mt-4">
              <h4 className="text-gray-800 dark:text-gray-200 text-[15px] font-medium">
                Email
              </h4>
              <div className="h-[50px] dark:bg-[#1F2B36] dark:text-gray-200 flex justify-start items-center bg-gray-50  dark:focus:bg-[#1F2B36]  w-full mt-2  px-[16px] font-manrope border rounded-xl  border-gray-200 dark:border-gray-600 outline-none  text-gray-700 transition duration-200">
                {userDetails?.user.email.address}
              </div>
            </div>
            <div className="mt-4">
              <h4 className="text-gray-800 dark:text-gray-200 text-[15px] font-medium">
                Date of Birth
              </h4>
              <div className="h-[50px] dark:bg-[#1F2B36] dark:text-gray-200 flex justify-start items-center bg-gray-50  dark:focus:bg-[#1F2B36]  w-full mt-2  px-[16px] font-manrope border rounded-xl  border-gray-200 dark:border-gray-600 outline-none  text-gray-700 transition duration-200">
                {userDetails?.user?.profile?.dateOfBirth ?? "----"}
              </div>
            </div>
            {!usernameAdded && (
              <div className=" justify-between mt-8 lgss:items-center">
                <div>
                  <h4 className="text-gray-800 dark:text-gray-200 text-[15px] font-medium">
                    Deex Tag
                  </h4>
                </div>
                <div className="flex justify-center items-start w-full  gap-2 mt-2">
                  <div className="w-[70%]">
                    <input
                      type="text"
                      value={deexTag}
                      onChange={(e) => {
                        let value = e.target.value;
                        setDeexTag(value);
                        setIsAvailable(null);
                      }}
                      placeholder="Enter your Deex tag"
                      className="h-[50px] dark:bg-[#1F2B36] dark:border-[#095B97] w-full text-[15px]  spin-button-none text-sm  px-[16px] font-manrope bg-[#F7F8F9] border rounded-lg border-[#617889] outline-none focus:border-blue-600 text-[#4E606E] dark:text-[#b8c3cc] transition duration-200"
                    />
                    {isAvailable === false ? (
                      <h4 className="italic text-[15px] mt-2 text-red-500">
                        {`${deexTag} is not available`}
                      </h4>
                    ) : isAvailable === true ? (
                      <h4 className="italic text-[15px] mt-2 text-green-500">
                        {`${deexTag} is available`}
                      </h4>
                    ) : null}
                  </div>
                  <button
                    onClick={handleAddUsername}
                    className="w-[30%] h-[50px] flex justify-center items-center bg-secondary_button text-white rounded-xl text-[16px]"
                  >
                    {completeSendTag.isPending ? (
                      <ClipLoader color="#ffffff" size={20} />
                    ) : (
                      <h4>Add tag</h4>
                    )}
                  </button>
                </div>
              </div>
            )}
            <div className="mt-4">
              <h4 className="text-gray-800 dark:text-gray-200 text-[15px] font-medium">
                Phone Number
              </h4>

              {userDetails?.user?.phone?.isVerified ? (
                <div className="flex h-[50px] justify-between mt-2 items-center w-full border px-4 dark:bg-[#1F2B36] dark:text-gray-200 bg-gray-50 dark:border-[#095B97] border-gray-200 rounded-xl">
                  <h4 className="text-gray-800 dark:text-gray-200 text-[15px] font-medium">
                    {userDetails?.user?.phone?.number}
                  </h4>
                  <BsCheck2Circle className="text-green-500 text-[20px]" />
                </div>
              ) : (
                <div className="flex h-[50px] justify-between mt-2 items-center w-full border px-4 dark:bg-[#1F2B36] dark:text-gray-200 bg-gray-50 dark:border-[#095B97] border-gray-200 rounded-xl">
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={phoneNumber}
                    maxLength={MAX_E164_DIGITS + 1}
                    onChange={(e) =>
                      setPhoneNumber(sanitizePhoneInput(e.target.value))
                    }
                    placeholder={
                      userDetails?.user?.phone?.number
                        ? userDetails?.user?.phone?.number
                        : "e.g. +447700900123 or 08012345678"
                    }
                    name="phonenumber"
                    className="flex justify-start items-center bg-transparent w-10/12 font-manrope outline-none text-gray-700 placeholder:text-gray-600 dark:placeholder:text-gray-300 transition duration-200"
                  />
                </div>
              )}
            </div>
            {!userDetails?.user?.phone?.isVerified && (
              <div className="text-[13px] mt-2 text-gray-500 dark:text-gray-400">
                Not verified.{" "}
                <button
                  onClick={() => {
                    setOpenEditProfile(false);
                    setOpenAddPhone(true);
                  }}
                  className="text-[#0D851D] font-bold"
                >
                  Verify Now
                </button>
              </div>
            )}
            <button
              disabled={
                completeSendProfileOtp.isPending ||
                ((isNameLocked || fullName.trim().length === 0) &&
                  !parseToE164Phone(phoneNumber))
              }
              onClick={() => {
                completeSendProfileOtp.mutate({
                  purpose: "edit-profile",
                });
              }}
              className={
                completeSendProfileOtp.isPending ||
                ((isNameLocked || fullName.trim().length === 0) &&
                  !parseToE164Phone(phoneNumber))
                  ? "w-full bg-gray-400 rounded-xl flex justify-center items-center text-gray-100 text-[15px] font-medium mt-12 h-[48px]"
                  : "w-full bg-secondary_button rounded-xl flex justify-center items-center text-white text-[15px] font-medium mt-12 h-[48px]"
              }
            >
              {completeSendProfileOtp.isPending ? (
                <ClipLoader color="#ffffff" size={20} />
              ) : (
                "Update profile"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditProfile;
